import { z } from 'zod';
import {
  ANNOUNCEMENT_AUDIENCE_TYPES,
  ANNOUNCEMENT_REACTIONS,
  ANNOUNCEMENT_STATUSES,
  DELIVERY_STATUSES,
  NOTIFICATION_CHANNELS,
  ROLES,
} from '../enums.js';
import {
  departmentRef,
  employeeRef,
  isoDateTime,
  listQueryWith,
  nonEmpty,
  objectId,
  richText,
  timestamps,
} from './common.js';

/** Notifications and announcements. */

// ── Notifications ───────────────────────────────────────────────────────────────

/**
 * Every event the system can raise. The catalog is the contract: a test asserts that
 * every key here has a dispatcher AND every dispatched key appears here, in both
 * directions. A dispatched-but-uncatalogued event is the classic silent failure — the
 * dispatch throws, a catch swallows it, and nobody is ever notified.
 */
export const NOTIFICATION_EVENTS = [
  'auth.passwordChanged',
  'auth.newDeviceLogin',
  'employee.invited',
  'employee.activated',
  'employee.probationDue',
  'attendance.punchReminder',
  'attendance.missingPunchOut',
  'attendance.correctionSubmitted',
  'attendance.correctionDecided',
  'leave.submitted',
  'leave.approved',
  'leave.rejected',
  'leave.cancelled',
  'leave.balanceAdjusted',
  'approval.pending',
  'approval.reassigned',
  'approval.overridden',
  'task.assigned',
  'task.reassigned',
  'task.deadlineApproaching',
  'task.overdue',
  'task.completed',
  'workReport.submitted',
  'workReport.reviewed',
  'workReport.changesRequested',
  'workReport.reminder',
  'announcement.published',
  'document.expiringSoon',
  'document.verified',
  'document.rejected',
  'onboarding.taskAssigned',
  'holiday.published',
  'workSession.interrupted',
  'workSession.staleTruncated',
  'system.jobFailed',
] as const;

export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number];

export const NOTIFICATION_CATEGORIES = [
  'leave',
  'attendance',
  'tasks',
  'workReports',
  'approvals',
  'announcements',
  'onboarding',
  'documents',
  'security',
  'system',
] as const;

export const notification = z.object({
  id: objectId,
  eventKey: z.enum(NOTIFICATION_EVENTS),
  category: z.enum(NOTIFICATION_CATEGORIES),
  title: z.string(),
  body: z.string(),
  redirectUrl: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  read: z.boolean(),
  readAt: isoDateTime.nullable(),
  dismissed: z.boolean(),
  createdAt: isoDateTime,
});

export const notificationListQuery = listQueryWith({
  read: z.coerce.boolean().optional(),
  category: z.enum(NOTIFICATION_CATEGORIES).optional(),
});

/**
 * Mutations return the AUTHORITATIVE unread count rather than leaving the client to
 * decrement locally — the usual cause of a badge that drifts out of step.
 */
export const unreadCount = z.object({ unreadCount: z.number().int().min(0) });

export const notificationPreferences = z.object({
  channels: z.object({
    inApp: z.literal(true).describe('In-app records are the source of truth and cannot be disabled'),
    email: z.boolean(),
    webPush: z.boolean(),
    desktop: z.boolean(),
  }),
  categories: z.record(z.enum(NOTIFICATION_CATEGORIES), z.boolean()),
  quietHours: z.object({
    enabled: z.boolean(),
    from: z.string().regex(/^\d{2}:\d{2}$/),
    to: z.string().regex(/^\d{2}:\d{2}$/),
  }),
  soundKey: z.string().nullable(),
  soundVolume: z.number().min(0).max(1),
});

export const updateNotificationPreferencesBody = z.object({
  channels: notificationPreferences.shape.channels.partial().optional(),
  categories: z.record(z.enum(NOTIFICATION_CATEGORIES), z.boolean()).optional(),
  quietHours: notificationPreferences.shape.quietHours.partial().optional(),
  soundKey: z.string().max(40).nullable().optional(),
  soundVolume: z.number().min(0).max(1).optional(),
});

/** Event × channel × role. Not every event goes to every channel by default. */
export const notificationPolicyRow = z.object({
  eventKey: z.enum(NOTIFICATION_EVENTS),
  channels: z.object({
    inApp: z.boolean(),
    email: z.boolean(),
    webPush: z.boolean(),
    desktop: z.boolean(),
  }),
  /** Empty means "whoever the event resolves to", not "everyone". */
  restrictToRoles: z.array(z.enum(ROLES)),
});

export const notificationPolicyBody = z.object({
  rows: z.array(notificationPolicyRow),
});

export const streamTicket = z.object({
  ticket: z.string(),
  expiresAt: isoDateTime,
});

export const pushSubscriptionBody = z.object({
  endpoint: z.string().url(),
  keys: z.object({ p256dh: z.string(), auth: z.string() }),
});

export const deliveryLogEntry = z.object({
  id: objectId,
  notificationId: objectId,
  eventKey: z.enum(NOTIFICATION_EVENTS),
  channel: z.enum(NOTIFICATION_CHANNELS),
  status: z.enum(DELIVERY_STATUSES),
  attempts: z.number().int(),
  lastAttemptAt: isoDateTime.nullable(),
  nextRetryAt: isoDateTime.nullable(),
  detail: z.string().nullable(),
  createdAt: isoDateTime,
});

// ── Announcements ───────────────────────────────────────────────────────────────

export const announcementAttachment = z.object({
  id: objectId,
  originalFilename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
});

export const announcementReactionSummary = z.object({
  counts: z.record(z.enum(ANNOUNCEMENT_REACTIONS), z.number().int()),
  total: z.number().int(),
  myReaction: z.enum(ANNOUNCEMENT_REACTIONS).nullable(),
});

export const announcement = z
  .object({
    id: objectId,
    title: z.string(),
    bodyHtml: z.string(),
    author: employeeRef,
    audienceType: z.enum(ANNOUNCEMENT_AUDIENCE_TYPES),
    audienceDepartments: z.array(departmentRef),
    audienceRoles: z.array(z.enum(ROLES)),
    status: z.enum(ANNOUNCEMENT_STATUSES),
    publishAt: isoDateTime.nullable(),
    expiresAt: isoDateTime.nullable(),
    publishedAt: isoDateTime.nullable(),
    requiresAcknowledgement: z.boolean(),
    attachments: z.array(announcementAttachment),
    reactions: announcementReactionSummary,
    /** Present for the reader; acknowledgement rosters are author/HR only. */
    readByMe: z.boolean(),
    acknowledgedByMe: z.boolean(),
    acknowledgementCount: z.number().int().nullable(),
  })
  .merge(timestamps);

export const createAnnouncementBody = z
  .object({
    title: nonEmpty('Title', 200),
    bodyHtml: richText,
    audienceType: z.enum(ANNOUNCEMENT_AUDIENCE_TYPES).default('organization'),
    audienceDepartmentIds: z.array(objectId).default([]),
    audienceRoles: z.array(z.enum(ROLES)).default([]),
    publishAt: isoDateTime.optional(),
    expiresAt: isoDateTime.optional(),
    requiresAcknowledgement: z.boolean().default(false),
    attachmentFileIds: z.array(objectId).default([]),
  })
  .refine(
    (v) => v.audienceType !== 'department' || v.audienceDepartmentIds.length > 0,
    { message: 'Choose at least one department', path: ['audienceDepartmentIds'] },
  )
  .refine((v) => v.audienceType !== 'role' || v.audienceRoles.length > 0, {
    message: 'Choose at least one role',
    path: ['audienceRoles'],
  });

export const updateAnnouncementBody = z.object({
  title: nonEmpty('Title', 200).optional(),
  bodyHtml: richText.optional(),
  audienceType: z.enum(ANNOUNCEMENT_AUDIENCE_TYPES).optional(),
  audienceDepartmentIds: z.array(objectId).optional(),
  audienceRoles: z.array(z.enum(ROLES)).optional(),
  publishAt: isoDateTime.nullable().optional(),
  expiresAt: isoDateTime.nullable().optional(),
  requiresAcknowledgement: z.boolean().optional(),
  attachmentFileIds: z.array(objectId).optional(),
});

export const setAnnouncementReactionBody = z.object({
  /** Omit or null to remove your reaction. */
  reaction: z.enum(ANNOUNCEMENT_REACTIONS).nullable(),
});

export const announcementListQuery = listQueryWith({
  status: z.enum(ANNOUNCEMENT_STATUSES).optional(),
});

export const acknowledgementRow = z.object({
  employee: employeeRef,
  readAt: isoDateTime.nullable(),
  acknowledgedAt: isoDateTime.nullable(),
});
